import { createDefaultPanel } from 'features/charts-v2/store'
import type { SelectedFilterOption } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type {
  Condition,
  FieldCatalogResponse,
} from 'features/charts-v2/types/yogiql'
import {
  fromYogiQLFilters,
  resolveYogiQLNames,
  toYogiQLFilters,
  warnYogiQLFallback,
  YOGIQL_VERSION,
} from 'features/charts-v2/utils'
import type {
  FeedbackQuery,
  FeedbackQueryPanel,
} from 'features/feedback/types/yogiql-page-state'
import type { FilterOption } from 'features/filters/types'
import type { FilterStateV2 } from 'types/project'

// ---------------------------------------------------------------------------
// Under `enhanced_dialect` a feedback panel is a YogiQL `query`. The sidebar,
// the carry-over and the legacy hash read still speak v2 filter fields, so
// these helpers read and write a query one "slot" at a time: the date, the
// search, or one v2 filter field. A slot's conditions are replaced together;
// every other condition is kept as it was.
// ---------------------------------------------------------------------------

/** A panel's filter fields: the v2 shape `fromYogiQLFilters` reads into. */
export type FilterQueryFields = Extract<
  ReturnType<typeof fromYogiQLFilters>,
  { ok: true }
>['fields']

/** What filters encode against: the hub's field catalog and filter options. */
export type FeedbackQueryCodec = {
  catalog: FieldCatalogResponse
  options: FilterOption[]
}

/** The catalog standard fields, the date and search encode against. */
export const EMPTY_CATALOG: FieldCatalogResponse = {
  yogiql: YOGIQL_VERSION,
  fields: [],
}

export type SearchFields = Pick<
  FilterQueryFields,
  'searchTerms' | 'searchCriteria' | 'smartSearchEnabled'
>

const NO_FIELDS: FilterQueryFields = {
  filters: [],
  dateRange: { startDate: 0, endDate: 0 },
  searchTerms: [],
  searchCriteria: {},
  smartSearchEnabled: false,
}

const DATE_SLOT = 'date'
const SEARCH_SLOT = 'search'
const KEPT_SLOT = 'kept:'

export const leafFields = (c: Condition): string[] => {
  if ('and' in c) return c.and.flatMap(leafFields)
  if ('or' in c) return c.or.flatMap(leafFields)
  if ('not' in c) return leafFields(c.not)
  return 'metric' in c ? [] : [c.field]
}

const onlyOn = (c: Condition, field: string): boolean => {
  const fields = leafFields(c)
  return fields.length > 0 && fields.every((f) => f === field)
}

export const isDateCondition = (c: Condition): boolean => onlyOn(c, 'date')
export const isSearchCondition = (c: Condition): boolean =>
  onlyOn(c, 'text_search')

/** A saved filter applied over `held`: its conditions replace them, keeping
 *  held's date when the saved filter carries none. */
export const withSavedQuery = (
  held: Condition[],
  saved: Condition[],
): Condition[] =>
  saved.some(isDateCondition)
    ? saved
    : [...saved, ...held.filter(isDateCondition)]

/** The slot a condition belongs to; one no v2 filter reads is kept apart. */
export function slotOf(c: Condition, options: FilterOption[]): string {
  if (isDateCondition(c)) return DATE_SLOT
  if (isSearchCondition(c)) return SEARCH_SLOT
  const read = fromYogiQLFilters([c], options)
  const field = read.ok ? read.fields.filters[0]?.field : undefined
  return field ?? `${KEPT_SLOT}${JSON.stringify(c)}`
}

export const inSlot =
  (slot: string, options: FilterOption[]) =>
  (c: Condition): boolean =>
    slotOf(c, options) === slot

/** `filters` with the conditions `matches` picks replaced by `next`, in place. */
export function replaceWhere(
  filters: Condition[],
  matches: (c: Condition) => boolean,
  next: Condition[],
): Condition[] {
  const at = filters.findIndex(matches)
  const kept = filters.filter((c) => !matches(c))
  if (at === -1) return [...kept, ...next]
  const before = filters.slice(0, at).filter((c) => !matches(c)).length
  return [...kept.slice(0, before), ...next, ...kept.slice(before)]
}

/** The query read as v2 filter fields, for the UI that still speaks them. */
export function readQuery(
  filters: Condition[],
  options: FilterOption[],
): FilterQueryFields {
  const readable = filters.filter(
    (c) => !slotOf(c, options).startsWith(KEPT_SLOT),
  )
  const read = fromYogiQLFilters(readable, options)
  return read.ok ? read.fields : NO_FIELDS
}

/** A query panel as the v2 `ChartPanel` the page's v2 readers take. */
export const queryToPanel = (
  panel: FeedbackQueryPanel,
  order: number,
  options: FilterOption[],
): ChartPanel => ({
  ...createDefaultPanel(order),
  id: panel.id,
  ...readQuery(panel.query.filters, options),
})

const encode = (
  fields: Partial<FilterQueryFields>,
  catalog: FieldCatalogResponse,
  options: FilterOption[],
): Condition[] | string => {
  const panel = { ...createDefaultPanel(0), ...NO_FIELDS, ...fields }
  return toYogiQLFilters(panel, catalog, resolveYogiQLNames(panel, options))
}

/** v2 filters as conditions, or why one has no encoding. */
export const encodeFilters = (
  filters: SelectedFilterOption[],
  codec: FeedbackQueryCodec,
): Condition[] | string => encode({ filters }, codec.catalog, codec.options)

/** Each filter that encodes; one that does not is dropped with a warning. */
export function encodeEachFilter(
  filters: SelectedFilterOption[],
  codec: FeedbackQueryCodec,
): Condition[] {
  return filters.flatMap((filter) => {
    const conditions = encodeFilters([filter], codec)
    if (typeof conditions !== 'string') return conditions
    warnYogiQLFallback(`feedback filter dropped: ${conditions}`)
    return []
  })
}

// The date and search never need the catalog or names, so never refuse.
const encodeFixed = (fields: Partial<FilterQueryFields>): Condition[] => {
  const conditions = encode(fields, EMPTY_CATALOG, [])
  return typeof conditions === 'string' ? [] : conditions
}

export const encodeDate = (
  dateRange: FilterQueryFields['dateRange'],
): Condition[] => encodeFixed({ dateRange })

export const encodeSearch = ({
  searchTerms,
  searchCriteria,
  smartSearchEnabled,
}: SearchFields): Condition[] =>
  encodeFixed({ searchTerms, searchCriteria, smartSearchEnabled })

/** A whole panel's filter fields as conditions: filters, date, then search. */
export const fieldsToConditions = (
  fields: FilterQueryFields,
  codec: FeedbackQueryCodec,
): Condition[] => [
  ...encodeEachFilter(fields.filters, codec),
  ...encodeDate(fields.dateRange),
  ...encodeSearch(fields),
]

export const toFeedbackQuery = (filters: Condition[]): FeedbackQuery => ({
  yogiql: YOGIQL_VERSION,
  filters,
})

/** A fresh panel's query: the default date window and nothing else. */
export const defaultFeedbackQuery = (): FeedbackQuery =>
  toFeedbackQuery(encodeDate(createDefaultPanel(0).dateRange))

/** The carried-over global filter state as a panel's conditions. */
export const globalStateToConditions = (
  state: FilterStateV2,
  codec: FeedbackQueryCodec,
): Condition[] =>
  fieldsToConditions(
    {
      filters: state.filters as SelectedFilterOption[],
      dateRange: state.dateRange,
      searchTerms: state.searchTerms,
      searchCriteria: state.searchCriteria,
      // Absent means off, as `applyGlobalFilterStateToPanel` reads it.
      smartSearchEnabled: state.smartSearchEnabled ?? false,
    },
    codec,
  )
